// i18n для admin SPA Editorial. Минимальная реализация — Zustand store +
// объект переводов EN/RU + хук useT(). Дефолт — EN. Юзер переключает в
// онбординге или Settings → Interface. Выбор персистится в localStorage.
//
// Файл монолитный — переводы хранятся в одном объекте. Если разрастётся
// до 500+ ключей, разнесём по разделам через namespacing. Сейчас ~200
// ключей, в один файл удобно читать diff'ы.

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type Language = "en" | "ru";

const en = {
  // ── App shell / Sidebar ────────────────────────────────────────────────
  appBrand: "Reverium",
  appSubBrand: "Editorial",
  navDashboard: "Dashboard",
  navArticles: "Articles",
  navDrafts: "Drafts",
  navScheduled: "Scheduled",
  navSeries: "Series",
  navTags: "Tags",
  navMedia: "Media Library",
  navAudit: "Audit log",
  navSettings: "Settings",
  signOut: "Sign out",
  collapseSidebar: "Collapse",
  expandSidebar: "Expand sidebar",

  // ── Common ─────────────────────────────────────────────────────────────
  save: "Save",
  cancel: "Cancel",
  delete: "Delete",
  edit: "Edit",
  create: "Create",
  add: "Add",
  remove: "Remove",
  search: "Search",
  loading: "Loading…",
  empty: "Nothing here yet",
  open: "Open",
  close: "Close",
  yes: "Yes",
  no: "No",
  next: "Next",
  back: "Back",
  done: "Done",

  // ── Login ──────────────────────────────────────────────────────────────
  loginTitle: "Sign in to Editorial",
  loginSubtitle: "Editor access only.",
  loginEmail: "Email",
  loginPassword: "Password",
  loginBtn: "Sign in",
  loginBtnBusy: "Signing in…",
  loginErrorBad: "Invalid email or password.",
  loginErrorInactive: "Your editor account is deactivated. Contact admin.",
  loginErrorGeneric: "Something went wrong. Try again.",

  // ── Nav (additional) ────────────────────────────────────────────────────
  navAnalytics: "Analytics",

  // ── Dashboard ──────────────────────────────────────────────────────────
  dashTitle: "Dashboard",
  dashKpiPublished: "Published",
  dashKpiDrafts: "Drafts",
  dashKpiViews: "Total views",
  dashKpiArticles: "Total articles",
  dashRecentActivity: "Recent activity",
  dashDraftsInProgress: "Drafts in progress",
  dashUpcomingSchedule: "Upcoming schedule",
  dashNoActivity: "No activity yet.",
  dashNoDrafts: "No drafts in progress.",
  dashNoScheduled: "Nothing scheduled.",
  dashSubtitle: "Editorial activity at a glance.",
  dashViewAnalytics: "Lookbook analytics",

  // ── Analytics ──────────────────────────────────────────────────────────
  analyticsTitle: "Analytics",
  analyticsSubtitle: "Lookbook pageviews, audience, and traffic sources.",
  analyticsDays: "d",
  analyticsKpiViews: "Pageviews",
  analyticsKpiPages: "Unique pages",
  analyticsKpiAvgDay: "Avg / day",
  analyticsKpiComments: "Comments",
  analyticsDailyViews: "Daily views",
  analyticsTopPages: "Top lookbooks",
  analyticsReferrers: "Traffic sources",
  analyticsAudience: "Audience",
  analyticsLangs: "Languages",
  analyticsScreens: "Screens",
  analyticsNoData: "No pageview data yet.",
  analyticsNoReferrers: "No referrer data yet.",

  // Devlog journal analytics
  analyticsDevlogTitle: "Devlog · Reading sessions",
  analyticsDevlogSubtitle: "How many people actually read the journal, how far they scroll, which reel they finish on.",
  analyticsDevlogSessions: "Sessions",
  analyticsDevlogCompleted: "Completed reads",
  analyticsDevlogAvgDwell: "Avg dwell time",
  analyticsDevlogAvgScroll: "Avg scroll depth",
  analyticsDevlogReels: "Reel distribution",
  analyticsDevlogTimeSeries: "Sessions per day",

  // ── Articles ───────────────────────────────────────────────────────────
  articlesTitle: "Articles",
  articlesNew: "+ New article",
  articlesViewGrid: "Grid",
  articlesViewList: "List",
  articlesFilterStatus: "Status",
  articlesFilterKind: "Kind",
  articlesSearchPlaceholder: "Search by title…",
  articlesBulkPublish: "Publish",
  articlesBulkArchive: "Archive",
  articlesBulkDelete: "Delete",
  articlesNothingFound: "No articles match your filters.",
  articlesSelectedN: (n: number) => `${n} selected`,
  articlesEmptyTitle: "No articles yet",
  articlesEmptyHint: "Click + New article to create your first drop.",
  articlesSubtitle: (n: number) => `${n} ${n === 1 ? "article" : "articles"}`,
  articlesBulkClear: "Clear",
  articlesColTitle: "Title",
  articlesColAuthor: "Author",
  articlesColUpdated: "Updated",
  articlesColViews: "Views",

  // ── Status badges ──────────────────────────────────────────────────────
  statusDraft: "Draft",
  statusReview: "In review",
  statusScheduled: "Scheduled",
  statusPublished: "Published",
  statusArchived: "Archived",

  // ── Article kinds ──────────────────────────────────────────────────────
  kindVideo: "Video",
  kindArticle: "Article",
  kindVisualEssay: "Visual essay",

  // ── ArticleEdit ────────────────────────────────────────────────────────
  editBack: "Back to articles",
  editTitlePlaceholder: "Article title…",
  editSummaryPlaceholder: "One-line summary…",
  editPreview: "Preview",
  editHidePreview: "Hide preview",
  editShortcuts: "Keyboard shortcuts",
  editSaveDraft: "Save draft",
  editPublishNow: "Publish now",
  editScheduleFor: "Schedule for…",
  editSaving: "Saving…",
  editSaved: "Saved",
  editSavedAgo: (ago: string) => `Saved ${ago}`,
  editUnsaved: "Unsaved changes",
  editSaveFailed: "Save failed",
  editWords: (n: number) => `${n} words`,
  editReadTime: (m: number) => `~${m} min read`,
  editLoadFailed: "Failed to load article",
  editSaveFailedPrefix: "Failed to save",

  // ── Shortcuts cheat-sheet (modal in ArticleEdit) ───────────────────────
  shortcutsTitle: "Keyboard shortcuts",
  shortcutsSave: "Save draft",
  shortcutsUndo: "Undo",
  shortcutsRedo: "Redo",
  shortcutsInsertBlock: "Insert block",
  shortcutsPublish: "Publish now",
  shortcutsClose: "Close",
  shortcutsCheatTitle: "?",

  // ── Block editor ───────────────────────────────────────────────────────
  blockMenuTitle: "Add a block",
  blockMenuSearch: "Search blocks…",
  blockCategoryAll: "All",
  blockCategoryText: "Text",
  blockCategoryMedia: "Media",
  blockCategoryEmbed: "Embed",
  blockCategoryLayout: "Layout",
  blockCategoryTemplates: "Templates",
  blockInsertTemplate: "Insert template",
  blockToolbarMoveUp: "Move up",
  blockToolbarMoveDown: "Move down",
  blockToolbarDuplicate: "Duplicate",
  blockToolbarDelete: "Delete",
  blockToolbarChangeType: "Change type",

  // Block types (для меню и toolbar):
  blockTypeHeading: "Heading",
  blockTypeParagraph: "Paragraph",
  blockTypeQuote: "Quote",
  blockTypeImage: "Image",
  blockTypeGallery: "Gallery",
  blockTypeVideoEmbed: "Video embed",
  blockTypeVideoFile: "Video file",
  blockTypeAudioEmbed: "Audio embed",
  blockTypeAudioFile: "Audio file",
  blockTypeLinkCard: "Link card",
  blockTypeCaption: "Caption",
  blockTypeDivider: "Divider",
  blockTypeList: "List",
  blockTypeSpacer: "Spacer",
  blockTypeCta: "CTA button",
  blockTypeHero: "Hero",
  blockTypeEmbed: "Custom embed",

  // Block descriptions (1-liner under each entry in BlockMenu)
  blockDescHeading: "Section break with bold type",
  blockDescParagraph: "Plain or rich text",
  blockDescQuote: "Pullquote or block quotation",
  blockDescImage: "Single image, file or URL",
  blockDescGallery: "Grid or masonry of images",
  blockDescVideoEmbed: "YouTube, Vimeo, Instagram, TikTok link",
  blockDescVideoFile: "Uploaded MP4 with controls",
  blockDescAudioEmbed: "SoundCloud, Spotify, Bandcamp, Apple Music, Artlist",
  blockDescAudioFile: "Uploaded MP3 or M4A",
  blockDescLinkCard: "Auto-fetched OG preview card",
  blockDescCaption: "Subtle text under media",
  blockDescDivider: "Horizontal rule or ornament",
  blockDescList: "Bullet or numbered",
  blockDescSpacer: "Vertical empty space",
  blockDescCta: "Big bronze action button",
  blockDescHero: "Full-bleed top banner",
  blockDescEmbed: "Raw iframe HTML (Twitter, custom)",
  blockMenuHeadingLabel: "Blocks",
  blockMenuNoMatches: "No matches.",
  blockHeadingLevel: (level: number) => `Heading ${level}`,

  // ── Settings panel (article sidebar) ───────────────────────────────────
  settingsPanelTabSettings: "Settings",
  settingsPanelTabAudit: "Audit",
  settingsPanelCover: "Cover",
  settingsPanelUploadCover: "Upload cover",
  settingsPanelRemoveCover: "Remove",
  settingsPanelKind: "Kind",
  settingsPanelStatus: "Status",
  settingsPanelSchedule: "Schedule",
  settingsPanelTags: "Tags",
  settingsPanelAuthor: "Author",
  settingsPanelSeries: "Series",
  settingsPanelSeriesNone: "— Standalone —",
  settingsPanelSeriesPosition: "Position (1-based)",
  settingsPanelPremium: "Premium-only",
  settingsPanelFeatured: "Featured on home",
  settingsPanelReplaceCover: "Replace…",
  settingsPanelScheduleHint: "If status = scheduled, auto-publishes at this time.",
  settingsPanelSeriesHint:
    "Optional. Group this article with related parts; readers get Prev/Next + TOC at the end.",
  settingsPanelVisibility: "Visibility",
  settingsPanelMetadata: "Metadata",
  settingsPanelViews: "Views",
  settingsPanelCreated: "Created",
  settingsPanelUpdated: "Updated",
  settingsPanelPublished: "Published",
  settingsPanelSaveFirstHint: "Save the article first to access these settings.",

  // ── Drafts / Scheduled ─────────────────────────────────────────────────
  draftsTitle: "Drafts",
  draftsEmpty: "All caught up — no drafts in progress.",
  draftsSubtitle: (n: number) => `${n} unfinished ${n === 1 ? "piece" : "pieces"}`,
  scheduledTitle: "Scheduled",
  scheduledEmpty: "Nothing scheduled.",
  scheduledPublishesIn: (h: number) => `publishes in ${h}h`,
  scheduledSubtitle: (n: number) => `${n} pending ${n === 1 ? "publish" : "publishes"}`,
  scheduledOverdue: "Overdue",

  // ── Series ─────────────────────────────────────────────────────────────
  seriesTitle: "Series",
  seriesIntro:
    "Group related articles into a cycle. Readers get Prev/Next navigation and a table of contents at the end of each part.",
  seriesNewPlaceholder: "New series title…",
  seriesNewBtn: "Add",
  seriesCount: (n: number) => `${n} article${n === 1 ? "" : "s"}`,
  seriesEmpty: "No series yet. Create your first one above.",
  seriesSelectHint: "Select a series from the left, or create a new one.",
  seriesArticlesIn: (n: number) => `Articles in this series (${n})`,
  seriesAddArticles: (n: number) => `Add articles (${n} unattached)`,
  seriesDelete: "Delete series",
  seriesDescPlaceholder: "Optional description…",
  seriesLabelTitle: "Title",
  seriesLabelDescription: "Description",
  seriesNoArticlesAttached: "No articles attached yet — pick from below to add.",

  // ── Tags ───────────────────────────────────────────────────────────────
  tagsTitle: "Tags",
  tagsNewName: "Tag name…",
  tagsAdd: "Add tag",
  tagsCount: (n: number) => `${n} article${n === 1 ? "" : "s"}`,
  tagsColor: "Color",
  tagsSlug: "Slug",
  tagsEmpty: "No tags yet. Add your first above.",
  tagsDelete: "Delete tag",
  tagsLabelName: "Name",
  tagsSaving: "Saving…",

  // ── Media Library ──────────────────────────────────────────────────────
  mediaTitle: "Media Library",
  mediaFilterKind: "Kind",
  mediaFilterAll: "All",
  mediaFilterImage: "Images",
  mediaFilterVideo: "Videos",
  mediaFilterAudio: "Audio",
  mediaFilterOther: "Other",
  mediaSearch: "Search by filename…",
  mediaSortDate: "By date",
  mediaSortName: "By name",
  mediaUsedIn: "Used in",
  mediaRemoveFromArticle: "Remove from article",
  mediaNothingFound: "No files match your filters.",
  mediaCount: (n: number, m: number) => `${n} of ${m} ${m === 1 ? "file" : "files"}`,
  mediaSortOldest: "Oldest first",
  mediaOpenOriginal: "Open original",
  mediaRemoving: "Removing…",

  // ── Audit log ──────────────────────────────────────────────────────────
  auditTitle: "Audit log",
  auditFilterAction: "Action",
  auditFilterActor: "Actor",
  auditFilterFrom: "From",
  auditFilterTo: "To",
  auditEmpty: "No log entries yet.",
  auditViewDetails: "Show diff",
  auditSubtitle: "Full history of edits across articles, tags and series.",
  auditAllActors: "All editors",
  auditReset: "Reset",
  auditHideDetails: "Hide",
  auditShowDetails: "Details",
  auditNoDiff: "No diff stored for this entry.",

  // ── Settings page ──────────────────────────────────────────────────────
  settingsTitle: "Settings",
  settingsProfile: "Profile",
  settingsName: "Name",
  settingsBio: "Bio",
  settingsAvatar: "Avatar",
  settingsUploadAvatar: "Upload avatar",
  settingsPassword: "Password",
  settingsOldPassword: "Current password",
  settingsNewPassword: "New password",
  settingsChangePassword: "Change password",
  settingsPasswordChanged: "Password changed.",
  settingsPasswordError: "Couldn't change password.",
  settingsLanguage: "Language",
  settingsLanguageHint:
    "Switch the interface language. Affects this admin only — readers' Editorial in the Reverium app has its own language setting.",
  settingsEditors: "Editors",
  settingsEditorsCount: (n: number) => `${n} editor${n === 1 ? "" : "s"}`,
  settingsInviteEditor: "Invite editor",
  settingsInviteEmail: "Editor's email",
  settingsInviteCreate: "Create with temp password",
  settingsOnboarding: "Onboarding tour",
  settingsOnboardingHint:
    "Replay the welcome tour any time — useful when onboarding new editors on this machine.",
  settingsReplayOnboarding: "Replay onboarding",
  settingsSaveProfile: "Save profile",
  settingsProfileSaved: "Profile saved.",

  // ── Onboarding ─────────────────────────────────────────────────────────
  onbSkip: "Skip tour",
  onbBack: "Back",
  onbNext: "Next",
  onbStart: "Start working",

  onbStep1Title: "Welcome to Editorial admin",
  onbStep1Body:
    "This is where you write and publish drops for the Reverium Editorial feed. Each article lives as structured blocks — text, video, audio, galleries, link cards. Readers see it inside the desktop app and can add any piece to their library with one click.",
  onbStep1Bullet1: "All changes are auto-saved every 10 seconds + on ⌘S.",
  onbStep1Bullet2: "Every edit is logged in the Audit log — your team can see who changed what.",
  onbStep1Bullet3: "Press ⌘K anywhere to jump between articles, tags, series, editors.",

  onbStep2Title: "Dashboard — your home base",
  onbStep2Body:
    "Quick health check of the feed. Four KPIs on top: published, drafts, total views, total articles. Below — recent activity stream, drafts you left in progress, and what's scheduled next.",
  onbStep2Bullet1: "Drafts in progress card — fastest way to jump back into half-written piece.",
  onbStep2Bullet2: "Upcoming schedule warns if a scheduled date is approaching with empty body.",

  onbStep3Title: "Articles — block-based editor",
  onbStep3Body:
    "17 block types covering text, media, embeds and layout. Type / inside a paragraph to open the block menu, or click + between blocks. Drag the handle on the left to reorder.",
  onbStep3Bullet1: "Magic Paste: paste markdown / Notion / Google Docs HTML — instantly parsed into blocks.",
  onbStep3Bullet2: "Image and gallery blocks accept both file upload AND external URL (Pinterest, Unsplash) — saves storage and respects rights.",
  onbStep3Bullet3: "Audio embeds: SoundCloud, Spotify, Bandcamp, Apple Music, Artlist — auto-detected from URL.",
  onbStep3Bullet4: "Link card with one URL → server fetches OG meta and fills title/description/image automatically.",
  onbStep3Bullet5: "Cmd+Z / Cmd+Shift+Z for undo/redo, Cmd+S to commit, ? to see all shortcuts.",

  onbStep4Title: "Series, Tags, Media Library",
  onbStep4Body:
    "Series turn related articles into a cycle (\"Light Series, Part 3 of 6\") with Prev/Next + TOC at the end of each part. Tags are open taxonomy with color swatches. Media Library aggregates every file uploaded across all articles — you can search, filter, and remove unused.",
  onbStep4Bullet1: "Position field in Series settings controls order — Prev/Next follows it.",
  onbStep4Bullet2: "Tag color shows up as accent chip in the reader's view.",
  onbStep4Bullet3: "Media Library shows where each file is used — safe to delete only if list is empty.",

  onbStep5Title: "Drafts, Scheduled, Audit log",
  onbStep5Body:
    "Three filtered views over articles. Drafts — work in progress. Scheduled — articles with a publish_at in the future. Audit log — full history of edits, with diff per record.",
  onbStep5Bullet1: "Scheduled auto-publishes at the timestamp — no manual click needed.",
  onbStep5Bullet2: "Audit log filters: by action type, by editor, by date range.",
  onbStep5Bullet3: "Click an entry to expand and see the actual field diff.",

  onbStep6Title: "Settings: profile, editors, language",
  onbStep6Body:
    "Manage your own profile (name, bio, avatar, password). Invite new editors with a temp password. Switch language (EN / RU) — also lives here. Replay this tour any time.",
  onbStep6Bullet1: "Invite editor → creates a new editors record with a one-time password you copy and send.",
  onbStep6Bullet2: "Password change is two-field: current + new.",
  onbStep6Bullet3: "Language switch is per-machine — won't affect your teammates.",

  onbStep7Title: "Ready to write",
  onbStep7Body:
    "That's the tour. Hit ⌘K anywhere to jump around. Open the keyboard shortcuts cheat-sheet from the editor's ? button. Replay this tour from Settings → Onboarding tour.",
  onbStep7CtaCreate: "Create my first article",

  onbLanguageSwitcherTooltip: "Language",
};

const ru: typeof en = {
  // ── App shell / Sidebar ────────────────────────────────────────────────
  appBrand: "Reverium",
  appSubBrand: "Editorial",
  navDashboard: "Главная",
  navAnalytics: "Аналитика",
  navArticles: "Статьи",
  navDrafts: "Черновики",
  navScheduled: "Запланированные",
  navSeries: "Серии",
  navTags: "Теги",
  navMedia: "Медиатека",
  navAudit: "Журнал действий",
  navSettings: "Настройки",
  signOut: "Выйти",
  collapseSidebar: "Свернуть",
  expandSidebar: "Развернуть",

  // ── Common ─────────────────────────────────────────────────────────────
  save: "Сохранить",
  cancel: "Отмена",
  delete: "Удалить",
  edit: "Редактировать",
  create: "Создать",
  add: "Добавить",
  remove: "Убрать",
  search: "Поиск",
  loading: "Загрузка…",
  empty: "Здесь пока пусто",
  open: "Открыть",
  close: "Закрыть",
  yes: "Да",
  no: "Нет",
  next: "Дальше",
  back: "Назад",
  done: "Готово",

  // ── Login ──────────────────────────────────────────────────────────────
  loginTitle: "Вход в Editorial",
  loginSubtitle: "Только для редакторов.",
  loginEmail: "Email",
  loginPassword: "Пароль",
  loginBtn: "Войти",
  loginBtnBusy: "Входим…",
  loginErrorBad: "Неверный email или пароль.",
  loginErrorInactive: "Аккаунт отключён. Свяжитесь с админом.",
  loginErrorGeneric: "Что-то пошло не так. Попробуйте ещё раз.",

  // ── Dashboard ──────────────────────────────────────────────────────────
  dashTitle: "Главная",
  dashKpiPublished: "Опубликовано",
  dashKpiDrafts: "Черновики",
  dashKpiViews: "Всего просмотров",
  dashKpiArticles: "Всего статей",
  dashRecentActivity: "Последние действия",
  dashDraftsInProgress: "Черновики в работе",
  dashUpcomingSchedule: "Ближайшие публикации",
  dashNoActivity: "Действий ещё не было.",
  dashNoDrafts: "Нет черновиков в работе.",
  dashNoScheduled: "Запланированных нет.",
  dashSubtitle: "Активность Editorial одним взглядом.",
  dashViewAnalytics: "Аналитика лукбуков",

  // ── Analytics ──────────────────────────────────────────────────────────
  analyticsTitle: "Аналитика",
  analyticsSubtitle: "Просмотры лукбуков, аудитория и источники трафика.",
  analyticsDays: "д",
  analyticsKpiViews: "Просмотры",
  analyticsKpiPages: "Уникальных страниц",
  analyticsKpiAvgDay: "Среднее / день",
  analyticsKpiComments: "Комментарии",
  analyticsDailyViews: "Просмотры по дням",
  analyticsTopPages: "Топ лукбуков",
  analyticsReferrers: "Источники трафика",
  analyticsAudience: "Аудитория",
  analyticsLangs: "Языки",
  analyticsScreens: "Экраны",
  analyticsNoData: "Данных по просмотрам пока нет.",
  analyticsNoReferrers: "Данных по рефереррам пока нет.",

  // Devlog journal analytics
  analyticsDevlogTitle: "Devlog · сессии чтения",
  analyticsDevlogSubtitle: "Сколько человек реально читают дневник, как далеко доскролливают, до какой катушки добираются.",
  analyticsDevlogSessions: "Сессии",
  analyticsDevlogCompleted: "Дочитали до конца",
  analyticsDevlogAvgDwell: "Среднее время на странице",
  analyticsDevlogAvgScroll: "Средняя глубина скролла",
  analyticsDevlogReels: "Распределение по катушкам",
  analyticsDevlogTimeSeries: "Сессии по дням",

  // ── Articles ───────────────────────────────────────────────────────────
  articlesTitle: "Статьи",
  articlesNew: "+ Новая статья",
  articlesViewGrid: "Плитка",
  articlesViewList: "Список",
  articlesFilterStatus: "Статус",
  articlesFilterKind: "Тип",
  articlesSearchPlaceholder: "Поиск по названию…",
  articlesBulkPublish: "Опубликовать",
  articlesBulkArchive: "В архив",
  articlesBulkDelete: "Удалить",
  articlesNothingFound: "Под фильтры ничего не подошло.",
  articlesSelectedN: (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 14) return `Выбрано ${n} статей`;
    if (m10 === 1) return `Выбрана ${n} статья`;
    if (m10 >= 2 && m10 <= 4) return `Выбрано ${n} статьи`;
    return `Выбрано ${n} статей`;
  },
  articlesEmptyTitle: "Статей ещё нет",
  articlesEmptyHint: "Нажмите + Новая статья, чтобы создать первый выпуск.",
  articlesSubtitle: (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 14) return `${n} статей`;
    if (m10 === 1) return `${n} статья`;
    if (m10 >= 2 && m10 <= 4) return `${n} статьи`;
    return `${n} статей`;
  },
  articlesBulkClear: "Снять выделение",
  articlesColTitle: "Название",
  articlesColAuthor: "Автор",
  articlesColUpdated: "Обновлена",
  articlesColViews: "Просмотры",

  // ── Status badges ──────────────────────────────────────────────────────
  statusDraft: "Черновик",
  statusReview: "На ревью",
  statusScheduled: "Запланировано",
  statusPublished: "Опубликовано",
  statusArchived: "В архиве",

  // ── Article kinds ──────────────────────────────────────────────────────
  kindVideo: "Видео",
  kindArticle: "Статья",
  kindVisualEssay: "Визуальное эссе",

  // ── ArticleEdit ────────────────────────────────────────────────────────
  editBack: "К списку статей",
  editTitlePlaceholder: "Название статьи…",
  editSummaryPlaceholder: "Короткое описание в одну строку…",
  editPreview: "Превью",
  editHidePreview: "Скрыть превью",
  editShortcuts: "Горячие клавиши",
  editSaveDraft: "Сохранить черновик",
  editPublishNow: "Опубликовать сейчас",
  editScheduleFor: "Запланировать на…",
  editSaving: "Сохраняем…",
  editSaved: "Сохранено",
  editSavedAgo: (ago: string) => `Сохранено ${ago}`,
  editUnsaved: "Несохранённые изменения",
  editSaveFailed: "Не удалось сохранить",
  editWords: (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 14) return `${n} слов`;
    if (m10 === 1) return `${n} слово`;
    if (m10 >= 2 && m10 <= 4) return `${n} слова`;
    return `${n} слов`;
  },
  editReadTime: (m: number) => `~${m} мин чтения`,
  editLoadFailed: "Не удалось загрузить статью",
  editSaveFailedPrefix: "Не удалось сохранить",

  // ── Shortcuts cheat-sheet (modal in ArticleEdit) ───────────────────────
  shortcutsTitle: "Горячие клавиши",
  shortcutsSave: "Сохранить черновик",
  shortcutsUndo: "Отменить",
  shortcutsRedo: "Повторить",
  shortcutsInsertBlock: "Вставить блок",
  shortcutsPublish: "Опубликовать",
  shortcutsClose: "Закрыть",
  shortcutsCheatTitle: "?",

  // ── Block editor ───────────────────────────────────────────────────────
  blockMenuTitle: "Добавить блок",
  blockMenuSearch: "Поиск блоков…",
  blockCategoryAll: "Все",
  blockCategoryText: "Текст",
  blockCategoryMedia: "Медиа",
  blockCategoryEmbed: "Embed",
  blockCategoryLayout: "Раскладка",
  blockCategoryTemplates: "Шаблоны",
  blockInsertTemplate: "Вставить шаблон",
  blockToolbarMoveUp: "Выше",
  blockToolbarMoveDown: "Ниже",
  blockToolbarDuplicate: "Дублировать",
  blockToolbarDelete: "Удалить",
  blockToolbarChangeType: "Сменить тип",

  blockTypeHeading: "Заголовок",
  blockTypeParagraph: "Параграф",
  blockTypeQuote: "Цитата",
  blockTypeImage: "Изображение",
  blockTypeGallery: "Галерея",
  blockTypeVideoEmbed: "Видео (ссылка)",
  blockTypeVideoFile: "Видео (файл)",
  blockTypeAudioEmbed: "Аудио (ссылка)",
  blockTypeAudioFile: "Аудио (файл)",
  blockTypeLinkCard: "Карточка ссылки",
  blockTypeCaption: "Подпись",
  blockTypeDivider: "Разделитель",
  blockTypeList: "Список",
  blockTypeSpacer: "Отступ",
  blockTypeCta: "Кнопка CTA",
  blockTypeHero: "Hero-блок",
  blockTypeEmbed: "Произвольный embed",

  // Block descriptions (1-liner под каждым блоком в BlockMenu)
  blockDescHeading: "Заголовок раздела",
  blockDescParagraph: "Обычный текст или с форматированием",
  blockDescQuote: "Цитата или блок-цитата",
  blockDescImage: "Одно изображение, файл или ссылка",
  blockDescGallery: "Сетка или masonry-галерея",
  blockDescVideoEmbed: "Ссылка на YouTube, Vimeo, Instagram, TikTok",
  blockDescVideoFile: "Загруженный MP4 с контролами",
  blockDescAudioEmbed: "SoundCloud, Spotify, Bandcamp, Apple Music, Artlist",
  blockDescAudioFile: "Загруженный MP3 или M4A",
  blockDescLinkCard: "Карточка с авто-fetch OG-meta",
  blockDescCaption: "Тонкая подпись под медиа",
  blockDescDivider: "Горизонтальная черта или орнамент",
  blockDescList: "Маркированный или нумерованный",
  blockDescSpacer: "Пустое вертикальное пространство",
  blockDescCta: "Большая бронзовая CTA-кнопка",
  blockDescHero: "Hero-баннер на всю ширину",
  blockDescEmbed: "Произвольный iframe (Twitter и др.)",
  blockMenuHeadingLabel: "Блоки",
  blockMenuNoMatches: "Ничего не найдено.",
  blockHeadingLevel: (level: number) => `Заголовок ${level}`,

  // ── Settings panel ─────────────────────────────────────────────────────
  settingsPanelTabSettings: "Настройки",
  settingsPanelTabAudit: "Действия",
  settingsPanelCover: "Обложка",
  settingsPanelUploadCover: "Загрузить обложку",
  settingsPanelRemoveCover: "Убрать",
  settingsPanelKind: "Тип",
  settingsPanelStatus: "Статус",
  settingsPanelSchedule: "Расписание",
  settingsPanelTags: "Теги",
  settingsPanelAuthor: "Автор",
  settingsPanelSeries: "Серия",
  settingsPanelSeriesNone: "— Без серии —",
  settingsPanelSeriesPosition: "Позиция (с 1)",
  settingsPanelPremium: "Только для подписчиков",
  settingsPanelFeatured: "На главную",
  settingsPanelReplaceCover: "Заменить…",
  settingsPanelScheduleHint: "Если статус = «Запланировано», публикация произойдёт автоматически в это время.",
  settingsPanelSeriesHint:
    "Необязательно. Объедините статью с другими частями серии — у читателей появится навигация Назад/Вперёд и оглавление.",
  settingsPanelVisibility: "Видимость",
  settingsPanelMetadata: "Метаданные",
  settingsPanelViews: "Просмотры",
  settingsPanelCreated: "Создана",
  settingsPanelUpdated: "Обновлена",
  settingsPanelPublished: "Опубликована",
  settingsPanelSaveFirstHint: "Сначала сохраните статью, чтобы открыть эти настройки.",

  // ── Drafts / Scheduled ─────────────────────────────────────────────────
  draftsTitle: "Черновики",
  draftsEmpty: "Всё в порядке — черновиков нет.",
  draftsSubtitle: (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 14) return `${n} черновиков в работе`;
    if (m10 === 1) return `${n} черновик в работе`;
    if (m10 >= 2 && m10 <= 4) return `${n} черновика в работе`;
    return `${n} черновиков в работе`;
  },
  scheduledTitle: "Запланированные",
  scheduledEmpty: "Запланированных нет.",
  scheduledPublishesIn: (h: number) => `публикация через ${h} ч`,
  scheduledSubtitle: (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 14) return `${n} публикаций ждут`;
    if (m10 === 1) return `${n} публикация ждёт`;
    if (m10 >= 2 && m10 <= 4) return `${n} публикации ждут`;
    return `${n} публикаций ждут`;
  },
  scheduledOverdue: "Просрочено",

  // ── Series ─────────────────────────────────────────────────────────────
  seriesTitle: "Серии",
  seriesIntro:
    "Объединяйте связанные статьи в циклы. Читатели получают навигацию Назад/Вперёд и оглавление в конце каждой части.",
  seriesNewPlaceholder: "Название новой серии…",
  seriesNewBtn: "Добавить",
  seriesCount: (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 14) return `${n} статей`;
    if (m10 === 1) return `${n} статья`;
    if (m10 >= 2 && m10 <= 4) return `${n} статьи`;
    return `${n} статей`;
  },
  seriesEmpty: "Серий пока нет. Создайте первую выше.",
  seriesSelectHint: "Выберите серию слева или создайте новую.",
  seriesArticlesIn: (n: number) => `Статей в серии (${n})`,
  seriesAddArticles: (n: number) => `Добавить статьи (свободных — ${n})`,
  seriesDelete: "Удалить серию",
  seriesDescPlaceholder: "Описание (необязательно)…",
  seriesLabelTitle: "Название",
  seriesLabelDescription: "Описание",
  seriesNoArticlesAttached: "Статей пока нет — выберите ниже, чтобы добавить.",

  // ── Tags ───────────────────────────────────────────────────────────────
  tagsTitle: "Теги",
  tagsNewName: "Название тега…",
  tagsAdd: "Добавить тег",
  tagsCount: (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 14) return `${n} статей`;
    if (m10 === 1) return `${n} статья`;
    if (m10 >= 2 && m10 <= 4) return `${n} статьи`;
    return `${n} статей`;
  },
  tagsColor: "Цвет",
  tagsSlug: "Slug",
  tagsEmpty: "Тегов ещё нет. Добавьте первый выше.",
  tagsDelete: "Удалить тег",
  tagsLabelName: "Название",
  tagsSaving: "Сохраняется…",

  // ── Media Library ──────────────────────────────────────────────────────
  mediaTitle: "Медиатека",
  mediaFilterKind: "Тип",
  mediaFilterAll: "Все",
  mediaFilterImage: "Изображения",
  mediaFilterVideo: "Видео",
  mediaFilterAudio: "Аудио",
  mediaFilterOther: "Прочее",
  mediaSearch: "Поиск по имени файла…",
  mediaSortDate: "По дате",
  mediaSortName: "По имени",
  mediaUsedIn: "Используется в",
  mediaRemoveFromArticle: "Убрать из статьи",
  mediaNothingFound: "Под фильтры ничего не подошло.",
  mediaCount: (n: number, m: number) => {
    const m10 = m % 10;
    const m100 = m % 100;
    let word: string;
    if (m100 >= 11 && m100 <= 14) word = "файлов";
    else if (m10 === 1) word = "файла";
    else if (m10 >= 2 && m10 <= 4) word = "файлов";
    else word = "файлов";
    // RU: "5 из 12 файлов" — слово согласуется с m (общим количеством).
    return `${n} из ${m} ${word}`;
  },
  mediaSortOldest: "Сначала старые",
  mediaOpenOriginal: "Открыть оригинал",
  mediaRemoving: "Удаляется…",

  // ── Audit log ──────────────────────────────────────────────────────────
  auditTitle: "Журнал действий",
  auditFilterAction: "Действие",
  auditFilterActor: "Кто",
  auditFilterFrom: "С",
  auditFilterTo: "По",
  auditEmpty: "Записей в журнале пока нет.",
  auditViewDetails: "Показать diff",
  auditSubtitle: "Полная история изменений по статьям, тегам и сериям.",
  auditAllActors: "Все редакторы",
  auditReset: "Сбросить",
  auditHideDetails: "Скрыть",
  auditShowDetails: "Детали",
  auditNoDiff: "Diff не сохранён для этой записи.",

  // ── Settings page ──────────────────────────────────────────────────────
  settingsTitle: "Настройки",
  settingsProfile: "Профиль",
  settingsName: "Имя",
  settingsBio: "О себе",
  settingsAvatar: "Аватар",
  settingsUploadAvatar: "Загрузить аватар",
  settingsPassword: "Пароль",
  settingsOldPassword: "Текущий пароль",
  settingsNewPassword: "Новый пароль",
  settingsChangePassword: "Сменить пароль",
  settingsPasswordChanged: "Пароль изменён.",
  settingsPasswordError: "Не удалось сменить пароль.",
  settingsLanguage: "Язык интерфейса",
  settingsLanguageHint:
    "Влияет только на эту админку. У читателей Editorial в Reverium-приложении свой переключатель.",
  settingsEditors: "Редакторы",
  settingsEditorsCount: (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m100 >= 11 && m100 <= 14) return `${n} редакторов`;
    if (m10 === 1) return `${n} редактор`;
    if (m10 >= 2 && m10 <= 4) return `${n} редактора`;
    return `${n} редакторов`;
  },
  settingsInviteEditor: "Пригласить редактора",
  settingsInviteEmail: "Email редактора",
  settingsInviteCreate: "Создать с временным паролем",
  settingsOnboarding: "Знакомство с админкой",
  settingsOnboardingHint:
    "Пройти ознакомительный тур заново — полезно для новых редакторов на этой машине.",
  settingsReplayOnboarding: "Пройти онбординг",
  settingsSaveProfile: "Сохранить профиль",
  settingsProfileSaved: "Профиль сохранён.",

  // ── Onboarding ─────────────────────────────────────────────────────────
  onbSkip: "Пропустить тур",
  onbBack: "Назад",
  onbNext: "Дальше",
  onbStart: "Начать работу",

  onbStep1Title: "Добро пожаловать в админку Editorial",
  onbStep1Body:
    "Здесь вы пишете и публикуете выпуски для ленты Reverium Editorial. Каждая статья — структурированные блоки: текст, видео, аудио, галереи, карточки ссылок. Читатели видят это в десктоп-приложении и могут одним кликом добавить любой элемент к себе в библиотеку.",
  onbStep1Bullet1: "Все изменения сохраняются автоматически каждые 10 секунд и на ⌘S.",
  onbStep1Bullet2: "Каждое действие пишется в Журнал — команда видит кто что менял.",
  onbStep1Bullet3: "Нажмите ⌘K в любом месте, чтобы быстро перейти к статье, тегу, серии или редактору.",

  onbStep2Title: "Главная — пульс ленты",
  onbStep2Body:
    "Быстрый обзор состояния. Четыре KPI наверху: опубликовано, черновиков, просмотров, всего статей. Ниже — последние действия команды, ваши черновики в работе и ближайшие публикации.",
  onbStep2Bullet1: "Карточка «Черновики в работе» — быстрый возврат к недописанной статье.",
  onbStep2Bullet2: "Раздел расписания предупредит, если дата близко, а тело статьи пустое.",

  onbStep3Title: "Статьи — блочный редактор",
  onbStep3Body:
    "17 типов блоков: текст, медиа, embeds и раскладка. Напечатайте / внутри параграфа — откроется меню блоков, либо нажмите + между блоками. Перетаскивайте за ручку слева для изменения порядка.",
  onbStep3Bullet1: "Magic Paste: вставьте markdown / контент из Notion / Google Docs — текст автоматически разложится в блоки.",
  onbStep3Bullet2: "Изображения и галереи принимают как файл, так и внешнюю ссылку (Pinterest, Unsplash) — экономит место и уважает права.",
  onbStep3Bullet3: "Аудио-embed: SoundCloud, Spotify, Bandcamp, Apple Music, Artlist — провайдер определяется по URL.",
  onbStep3Bullet4: "Link card: вставьте URL → сервер сам подтянет OG-мета и заполнит заголовок/описание/обложку.",
  onbStep3Bullet5: "Cmd+Z / Cmd+Shift+Z — отмена и повтор, Cmd+S — сохранить, ? — список всех шорткатов.",

  onbStep4Title: "Серии, Теги, Медиатека",
  onbStep4Body:
    "Серии превращают связанные статьи в цикл («Light Series, часть 3 из 6») с навигацией Назад/Вперёд и оглавлением в конце каждой части. Теги — открытая таксономия с цветовыми метками. Медиатека собирает все загруженные файлы из всех статей — можно искать, фильтровать и удалять неиспользуемое.",
  onbStep4Bullet1: "Поле «Позиция» в настройках серии задаёт порядок — навигация Назад/Вперёд следует ему.",
  onbStep4Bullet2: "Цвет тега показывается как акцентная метка в интерфейсе читателя.",
  onbStep4Bullet3: "Медиатека показывает где используется каждый файл — удалять безопасно только если список пустой.",

  onbStep5Title: "Черновики, Запланированные, Журнал",
  onbStep5Body:
    "Три отфильтрованных среза по статьям. Черновики — в работе. Запланированные — статьи с будущей датой публикации. Журнал — полная история правок с diff по каждой записи.",
  onbStep5Bullet1: "Запланированные публикуются автоматически в указанную дату — без ручного клика.",
  onbStep5Bullet2: "Фильтры в журнале: по типу действия, по редактору, по диапазону дат.",
  onbStep5Bullet3: "Кликните по записи — развернётся diff с реальными изменёнными полями.",

  onbStep6Title: "Настройки: профиль, редакторы, язык",
  onbStep6Body:
    "Управление профилем (имя, био, аватар, пароль). Приглашение новых редакторов с временным паролем. Переключатель языка интерфейса (EN/RU). Кнопка «Пройти онбординг» — повторить этот тур.",
  onbStep6Bullet1: "Пригласить редактора → создаётся запись в editors с одноразовым паролем, который вы копируете и отправляете.",
  onbStep6Bullet2: "Смена пароля — два поля: текущий и новый.",
  onbStep6Bullet3: "Язык интерфейса хранится локально — не влияет на коллег.",

  onbStep7Title: "Готово",
  onbStep7Body:
    "Это был тур. Нажмите ⌘K в любом месте для быстрого перехода. Список всех горячих клавиш — по кнопке ? в редакторе статьи. Повторить этот тур — Настройки → Знакомство с админкой.",
  onbStep7CtaCreate: "Создать первую статью",

  onbLanguageSwitcherTooltip: "Язык",
};

const dict: Record<Language, typeof en> = { en, ru };

export type T = typeof en;

interface LangStore {
  lang: Language;
  setLang: (l: Language) => void;
}

export const useLangStore = create<LangStore>()(
  persist(
    (set) => ({
      lang: "en",
      setLang: (lang) => set({ lang }),
    }),
    {
      name: "reverium-editorial-admin-lang",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

/** Returns the full translation object for the current admin language. */
export function useT(): T {
  const lang = useLangStore((s) => s.lang);
  return dict[lang];
}
